import { motion } from 'framer-motion'
import { Zap, Droplets, Shield, Armchair, ArrowRight } from 'lucide-react'

const ProductsSection = () => {
  const products = [
    {
      icon: Zap,
      title: 'Smart Energy Node (SEN)',
      subtitle: 'The "Energy Brain"',
      description: 'Transforms standard Battery Energy Storage Systems (BESS) into financial guardians through AI-driven predictive optimization.',
      features: [
        'Predictive Tier Protection using Weather APIs and Load Forecasting',
        'Tracks cumulative monthly kWh to prevent STEG billing tier jumps',
        'Guarantees 25% reduction in peak demand',
        'Protects against the "Tranche Trap"',
      ],
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: Droplets,
      title: 'Smart Water Digitizer (SWD)',
      subtitle: 'The "Resource Sense"',
      description: 'Non-invasive ESP32-CAM "clip-on" to digitize legacy analog meters through Computer Vision (OCR).',
      features: [
        'Fluid Intelligence - Automates meter reading without plumbing changes',
        'Leak Detection - Identifies leaks in milliseconds',
        'Total Transparency - Real-time visibility into consumption',
        '10–15% Reduction in water waste',
      ],
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Shield,
      title: 'AI-Retail Guard',
      subtitle: 'Behavioral Intelligence vs. The "Blind" POS',
      description: 'We stop theft that traditional scanners and cameras miss through real-time contextual validation.',
      features: [
        'Behavior Detection - AI detects concealment using edge-processed video',
        'POS Synchronization - Cross-references items with live transaction data',
        'Discreet Action - Triggers silent security alerts',
        'Direct margin protection through shrinkage reduction',
      ],
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Armchair,
      title: 'Industrial Robotics',
      subtitle: 'The "Precision Hands"',
      description: 'High-precision robotic arms (3 to 7 axes) designed for seamless industrial automation.',
      features: [
        'Connected Automation - Fully integrated into IoT ecosystem',
        'Smart Duty Cycles - Adjusts based on real-time power availability',
        'Industrial Applications - Assembly, repetitive tasks, warehouse logistics',
        'High reliability with real-time data exchange',
      ],
      color: 'from-green-500 to-emerald-500',
    },
  ]

  return (
    <section id="products" className="py-20 bg-background">
      <div className="section-container">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
            Our <span className="gradient-text">Products</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Complete Connected Intelligence ecosystem for industrial autonomy
          </p>
        </motion.div>

        {/* Products Grid */}
        <div className="space-y-16">
          {products.map((product, index) => (
            <motion.div
              key={product.title}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-12 items-center`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Image/Visual */}
              <div className="w-full lg:w-1/2">
                <motion.div
                  className={`relative aspect-video rounded-2xl overflow-hidden bg-gradient-to-br ${product.color} p-1`}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="absolute inset-0 bg-background rounded-2xl m-0.5" />
                  <div className="relative h-full flex items-center justify-center">
                    <product.icon
                      className={`text-8xl md:text-9xl`}
                      style={{ 
                        background: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    />
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="w-full lg:w-1/2 space-y-6">
                <motion.div
                  className={`inline-flex items-center space-x-2 bg-gradient-to-r ${product.color} rounded-full px-4 py-2`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <product.icon className="text-white" size={18} />
                  <span className="text-white font-medium text-sm">
                    {product.subtitle}
                  </span>
                </motion.div>

                <h3 className="text-3xl md:text-4xl font-display font-bold text-foreground">
                  {product.title}
                </h3>

                <p className="text-lg text-muted-foreground leading-relaxed">
                  {product.description}
                </p>

                <ul className="space-y-3">
                  {product.features.map((feature, i) => (
                    <motion.li
                      key={i}
                      className="flex items-start space-x-3 text-muted-foreground"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 * i }}
                    >
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${product.color} mt-2 flex-shrink-0`} />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                <motion.a
                  href="#contact"
                  className="inline-flex items-center space-x-2 text-primary font-semibold hover:underline"
                  whileHover={{ x: 5 }}
                >
                  <span>Learn More</span>
                  <ArrowRight size={20} />
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ProductsSection
